import { useSuspenseQuery, useQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'

export interface CreditStatusInvite {
  inviteId: string
  invitedEmail: string
  completedAt: string
  updatedAt: string
  userId: string
  profilePhoto: string
}

interface CreditStatusResponse {
  earnedCredits: number
  invitesTowardNextCredit: CreditStatusInvite[]
  pointsEarned: number
  invitesCompletedCount: number
}

const PATHNAME = '/users/invites/me/page'

/**
 * React Query hook that fetches the user's invite page data using suspense mode.
 * This includes earned lead credits, invites toward next credit, and points earned.
 */
export const useGetCreditStatus = () => {
  const { currentAuth } = useAuthState()

  return useSuspenseQuery<CreditStatusResponse>({
    ...queries.invites.creditStatus,
    queryFn: async () => {
      if (!currentAuth) {
        throw new Error('No current auth')
      }
      const response = await getRequest(PATHNAME)
      return response as CreditStatusResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: 1000,
  })
}

/**
 * Non-suspense version of the same hook for components that can't use Suspense
 */
export const useGetCreditStatusQuery = () => {
  const { currentAuth } = useAuthState()

  return useQuery<CreditStatusResponse>({
    ...queries.invites.creditStatus,
    queryFn: async () => {
      if (!currentAuth) {
        throw new Error('No current auth')
      }
      const response = await getRequest(PATHNAME)
      return response as CreditStatusResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: 1000,
    enabled: !!currentAuth,
  })
}
